import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit2, Wand2, ArrowLeft, Save, Eye, ExternalLink, Layout, Search, Database, Settings as SettingsIcon, Upload, CheckCircle2, X, Smartphone, Monitor } from "lucide-react";
import { ContentBuilder, SeoPanel, SchemaPanel } from "./admin-pages";
import { PageRenderer } from "../components/page-renderer";

function interpolateValue(s: any, ctx: Record<string, any>): any {
  if (typeof s === "string") return s.replace(/\{\{\s*([\w]+)\.([\w]+)\s*\}\}/g, (_m, ns, key) => {
    const v = ctx?.[ns]?.[key]; return v == null ? "" : String(v);
  });
  if (Array.isArray(s)) return s.map(x => interpolateValue(x, ctx));
  if (s && typeof s === "object") {
    const out: any = {};
    for (const k of Object.keys(s)) out[k] = interpolateValue(s[k], ctx);
    return out;
  }
  return s;
}

const API = `${import.meta.env.BASE_URL}api`;

type Template = {
  id: number; name: string; slugPattern: string;
  bindings: { var: string; collectionSlug: string; entrySlugs?: string[] }[];
  blocks: any[]; seoTitle: string | null; metaDescription: string | null;
  focusKeyphrase: string | null; keywords: string[] | null;
  featuredImageUrl: string | null; schemas: any[] | null;
  status: "draft" | "published"; noindex: boolean;
};

type Collection = { id: number; name: string; slug: string; fields: any[] };

function applySeoDefaults(t: Template): Template {
  const vars = (t.bindings || []).map(b => b.var).filter(Boolean);
  if (vars.length === 0) return t;
  const titleVars = vars.map(v => `{{${v}.title}}`).join(" ");
  const inPhrase = vars.length >= 2
    ? `${vars.slice(0, -1).map(v => `{{${v}.title}}`).join(" ")} in {{${vars[vars.length - 1]}.title}}`
    : titleVars;
  const patch: Partial<Template> = {};
  if (!t.focusKeyphrase || !t.focusKeyphrase.trim()) patch.focusKeyphrase = titleVars;
  if (!t.seoTitle || !t.seoTitle.trim()) patch.seoTitle = `${inPhrase} | VertexAir`;
  if (!t.metaDescription || !t.metaDescription.trim()) patch.metaDescription = `Get ${inPhrase} from VertexAir. Fast, professional service with a 12-month workmanship guarantee.`;
  if (!t.keywords || t.keywords.length === 0) patch.keywords = [titleVars, inPhrase];
  return Object.keys(patch).length ? { ...t, ...patch } : t;
}

export default function AdminProgrammatic() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);
  const [seedPreview, setSeedPreview] = useState<{ collections: number; collection_entries: number; programmatic_templates: number; pages: number } | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const load = () => fetch(`${API}/admin/templates`, { credentials: "include" }).then(r => r.json()).then(setTemplates);
  useEffect(() => { load(); }, []);
  useEffect(() => { fetch(`${API}/admin/seed-content/preview`, { credentials: "include" }).then(r => r.ok ? r.json() : null).then(setSeedPreview).catch(() => {}); }, []);

  const seed = async () => {
    if (!seedPreview) return;
    const total = seedPreview.collections + seedPreview.collection_entries + seedPreview.programmatic_templates + seedPreview.pages;
    if (!confirm(`Import ${total} rows (${seedPreview.collections} collections, ${seedPreview.collection_entries} entries, ${seedPreview.programmatic_templates} templates, ${seedPreview.pages} pages) into this database?\n\nExisting matching rows will be updated. This is safe to run multiple times.`)) return;
    setSeeding(true); setSeedResult(null);
    try {
      const r = await fetch(`${API}/admin/seed-content`, { method: "POST", credentials: "include" });
      const data = await r.json();
      if (data.ok) {
        setSeedResult({ ok: true, msg: `Imported ${Object.values(data.summary as Record<string, number>).reduce((a, b) => a + b, 0)} rows` });
        load();
      } else {
        setSeedResult({ ok: false, msg: data.error || "Failed" });
      }
    } catch (e: any) {
      setSeedResult({ ok: false, msg: String(e?.message || e) });
    } finally {
      setSeeding(false);
    }
  };

  const create = async () => {
    const r = await fetch(`${API}/admin/templates`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: "New Template",
        slugPattern: "{{service.slug}}-in-{{city.slug}}",
        bindings: [{ var: "service", collectionSlug: "services" }, { var: "city", collectionSlug: "cities" }],
        seoTitle: "{{service.title}} in {{city.title}} | VertexAir",
        metaDescription: "Get {{service.title}} in {{city.title}} from VertexAir. Fast, professional service with a 12-month workmanship guarantee.",
        focusKeyphrase: "{{service.title}} {{city.title}}",
        keywords: ["{{service.title}} {{city.title}}", "{{service.title}} in {{city.title}}"],
      }),
    });
    if (r.ok) { const t = await r.json(); load(); setOpenId(t.id); }
  };

  if (openId != null) return <TemplateEditor id={openId} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Wand2 className="w-6 h-6 text-primary" /> Programmatic SEO</h1>
          <p className="text-sm text-slate-500 mt-1">Generate hundreds of landing pages from one template + your collections.</p>
        </div>
        <div className="flex items-center gap-2">
          {seedPreview && (
            <button
              onClick={seed}
              disabled={seeding}
              title="Copy all templates, collections, entries and pages bundled with this deploy into the current database. Idempotent."
              className="px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg flex items-center gap-2 text-sm font-medium hover:border-primary hover:text-primary disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {seeding ? "Importing…" : `Import bundled content (${seedPreview.collections + seedPreview.collection_entries + seedPreview.programmatic_templates + seedPreview.pages} rows)`}
            </button>
          )}
          <button onClick={create} className="px-4 py-2 bg-primary text-white rounded-lg flex items-center gap-2 font-medium hover:opacity-90"><Plus className="w-4 h-4" /> New Template</button>
        </div>
      </div>
      {seedResult && (
        <div className={`mb-4 px-4 py-3 rounded-lg flex items-center gap-2 text-sm ${seedResult.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-rose-50 text-rose-800 border border-rose-200"}`}>
          <CheckCircle2 className="w-4 h-4" />
          {seedResult.msg}
        </div>
      )}
      {templates.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-dashed border-slate-300">
          <Wand2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 mb-1">No templates yet.</p>
          <p className="text-xs text-slate-400">Create collections first (Cities, Services), then templates to combine them.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {templates.map(t => (
            <button key={t.id} onClick={() => setOpenId(t.id)} className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-primary hover:shadow-sm transition">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-slate-900">{t.name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{t.status}</span>
              </div>
              <code className="text-xs text-primary block truncate">vertexclimate.co.uk/{t.slugPattern}</code>
              <p className="text-xs text-slate-400 mt-2">{(t.bindings || []).length} binding{(t.bindings || []).length === 1 ? "" : "s"}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ id, onBack }: { id: number; onBack: () => void }) {
  const [tpl, setTpl] = useState<Template | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [tab, setTab] = useState<"variables" | "content" | "seo" | "schema" | "settings">("variables");
  const [preview, setPreview] = useState<{ total: number; sample: any[]; firstContext?: Record<string, any> | null } | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "mobile">("desktop");
  const [previewIdx, setPreviewIdx] = useState(0);

  useEffect(() => {
    fetch(`${API}/admin/templates/${id}`, { credentials: "include" }).then(r => r.json()).then((t: Template) => {
      setTpl(applySeoDefaults(t));
    });
    fetch(`${API}/admin/collections`, { credentials: "include" }).then(r => r.json()).then(setCollections);
  }, [id]);

  const saveNow = async (snapshot?: Template | null) => {
    const data = snapshot ?? tpl;
    if (!data) return;
    setSaving(true);
    await fetch(`${API}/admin/templates/${id}`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
    setSaving(false); setSavedAt(new Date());
  };

  useEffect(() => {
    if (!tpl) return;
    const h = setTimeout(() => { saveNow(tpl); }, 600);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpl, id]);

  const refreshPreview = async () => {
    const r = await fetch(`${API}/admin/templates/${id}/preview`, { credentials: "include" });
    if (r.ok) setPreview(await r.json());
  };
  useEffect(() => { if (savedAt) refreshPreview(); }, [id, savedAt?.getTime()]);
  useEffect(() => { refreshPreview(); }, [id]);

  if (!tpl) return <div className="p-10 text-center text-slate-400">Loading...</div>;
  const upd = (p: Partial<Template>) => setTpl({ ...tpl, ...p });

  const publish = async () => {
    upd({ status: tpl.status === "published" ? "draft" : "published" });
  };

  const del = async () => {
    if (!confirm(`Delete template "${tpl.name}"?`)) return;
    await fetch(`${API}/admin/templates/${id}`, { method: "DELETE", credentials: "include" });
    onBack();
  };

  // build a fake page object so we can reuse the page editor's panels
  const fakePage = {
    ...tpl,
    title: tpl.name,
    slug: tpl.slugPattern,
    status: tpl.status,
    parentSlug: null, showInNav: false, navOrder: 0,
    ogImageUrl: tpl.featuredImageUrl, canonicalUrl: null,
  } as any;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-4 h-4" /></button>
            <input value={tpl.name} onChange={e => upd({ name: e.target.value })} className="font-semibold text-lg outline-none truncate" />
          </div>
          <div className="flex items-center gap-2">
            {saving ? <span className="text-xs text-slate-400">Saving…</span> : savedAt && <span className="text-xs text-slate-400">Saved</span>}
            <button onClick={() => setShowPreview(true)} className="px-3 py-1.5 bg-white border border-slate-200 hover:border-primary hover:text-primary rounded-lg text-sm font-medium flex items-center gap-1.5"><Eye className="w-4 h-4" /> Preview</button>
            <button onClick={() => saveNow()} disabled={saving} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg text-sm font-medium flex items-center gap-1.5"><Save className="w-4 h-4" /> Save</button>
            <button onClick={del} className="px-3 py-1.5 text-rose-600 hover:bg-rose-50 rounded-lg text-sm"><Trash2 className="w-4 h-4" /></button>
            <button onClick={publish} className={`px-4 py-2 rounded-lg text-sm font-medium ${tpl.status === "published" ? "bg-slate-200 text-slate-700" : "bg-primary text-white"}`}>{tpl.status === "published" ? "Unpublish" : "Publish"}</button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 flex gap-1 border-t border-slate-100">
          {([
            { k: "variables", l: "Variables", i: Wand2 },
            { k: "content", l: "Content", i: Layout },
            { k: "seo", l: "SEO", i: Search },
            { k: "schema", l: "Schema", i: Database },
            { k: "settings", l: "Settings", i: SettingsIcon },
          ] as const).map(t => (
            <button key={t.k} onClick={() => setTab(t.k as any)} className={`px-4 py-3 text-sm font-medium border-b-2 flex items-center gap-2 ${tab === t.k ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              <t.i className="w-4 h-4" /> {t.l}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4">
        {tab === "variables" && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
                <strong>How it works:</strong> Bind each variable in your URL pattern to a collection. Every entry combination will become a live page. Example: <code>{`{{service.slug}}-in-{{city.slug}}`}</code> with 5 services × 10 cities = 50 SEO pages.
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase">URL Pattern</label>
                  <div className="flex items-stretch mt-1 rounded-lg border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-primary/30">
                    <span className="px-3 flex items-center bg-slate-50 text-slate-500 font-mono text-sm border-r border-slate-200 whitespace-nowrap">vertexclimate.co.uk/</span>
                    <input value={tpl.slugPattern} onChange={e => upd({ slugPattern: e.target.value })} className="flex-1 px-3 py-2 font-mono text-sm outline-none" placeholder="{{service.slug}}-in-{{city.slug}}" />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Use lowercase slugs only. e.g. <code>{`{{service.slug}}-in-{{city.slug}}`}</code></p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase">Bindings</label>
                  <div className="space-y-2 mt-2">
                    {(tpl.bindings || []).map((b, i) => (
                      <div key={i} className="space-y-2 border border-slate-100 rounded-lg p-2">
                        <div className="flex gap-2 items-center">
                          <span className="text-slate-400 text-sm">{`{{`}</span>
                          <input value={b.var} onChange={e => upd({ bindings: tpl.bindings.map((x, j) => j === i ? { ...x, var: e.target.value } : x) })} placeholder="service" className="w-32 px-2 py-1.5 border border-slate-200 rounded text-sm font-mono" />
                          <span className="text-slate-400 text-sm">{`}}`} →</span>
                          <select value={b.collectionSlug} onChange={e => upd({ bindings: tpl.bindings.map((x, j) => j === i ? { ...x, collectionSlug: e.target.value, entrySlugs: [] } : x) })} className="flex-1 px-2 py-1.5 border border-slate-200 rounded text-sm">
                            <option value="">— Collection —</option>
                            {collections.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                          </select>
                          <button onClick={() => upd({ bindings: tpl.bindings.filter((_, j) => j !== i) })} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        {b.collectionSlug && (
                          <EntryFilter
                            collectionSlug={b.collectionSlug}
                            selected={b.entrySlugs || []}
                            onChange={(slugs) => upd({ bindings: tpl.bindings.map((x, j) => j === i ? { ...x, entrySlugs: slugs } : x) })}
                          />
                        )}
                      </div>
                    ))}
                    <button onClick={() => upd({ bindings: [...(tpl.bindings || []), { var: "", collectionSlug: "" }] })} className="text-sm text-primary font-medium">+ Add binding</button>
                  </div>
                </div>
              </div>
            </div>

            <PreviewPanel preview={preview} onRefresh={refreshPreview} />
          </div>
        )}
        {tab === "content" && (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 mb-3">
              💡 Use <code>{`{{service.title}}`}</code>, <code>{`{{city.title}}`}</code>, etc. in any text. They're replaced per generated page.
            </div>
            <ContentBuilder page={fakePage} onChange={(p: any) => upd({ blocks: p.blocks })} />
          </>
        )}
        {tab === "seo" && (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 mb-3">
              💡 Variables like <code>{`{{service.title}}`}</code> work in SEO title, meta description, and keyphrase too.
            </div>
            <SeoPanel page={fakePage} onChange={(p: any) => upd({ seoTitle: p.seoTitle, metaDescription: p.metaDescription, focusKeyphrase: p.focusKeyphrase, keywords: p.keywords, featuredImageUrl: p.featuredImageUrl })} />
          </>
        )}
        {tab === "schema" && <SchemaPanel schemas={tpl.schemas || []} onChange={s => upd({ schemas: s })} />}
        {tab === "settings" && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 max-w-xl">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={tpl.noindex} onChange={e => upd({ noindex: e.target.checked })} /> Noindex (don't include in sitemap or search engines)</label>
          </div>
        )}
      </div>

      {showPreview && (
        <PreviewModal
          tpl={tpl}
          preview={preview}
          device={previewDevice}
          setDevice={setPreviewDevice}
          idx={previewIdx}
          setIdx={setPreviewIdx}
          onClose={() => setShowPreview(false)}
          onRefresh={refreshPreview}
        />
      )}
    </div>
  );
}

function PreviewModal({ tpl, preview, device, setDevice, idx, setIdx, onClose, onRefresh }: {
  tpl: Template;
  preview: { total: number; sample: any[]; firstContext?: Record<string, any> | null } | null;
  device: "desktop" | "mobile";
  setDevice: (d: "desktop" | "mobile") => void;
  idx: number; setIdx: (n: number) => void;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const sample = preview?.sample || [];
  const safeIdx = Math.min(Math.max(0, idx), Math.max(0, sample.length - 1));
  const current = sample[safeIdx];
  // The endpoint returns full firstContext only for sample[0]; for others we have just {title, slug} per ns.
  const ctx = safeIdx === 0 && preview?.firstContext ? preview.firstContext : (current?.context || {});
  const renderedBlocks = useMemo(() => interpolateValue(tpl.blocks || [], ctx), [tpl.blocks, ctx]);
  const renderedTitle = useMemo(() => interpolateValue(tpl.seoTitle || tpl.name, ctx), [tpl.seoTitle, tpl.name, ctx]);
  const renderedMeta = useMemo(() => interpolateValue(tpl.metaDescription || "", ctx), [tpl.metaDescription, ctx]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex flex-col" onClick={onClose}>
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between flex-wrap gap-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 min-w-0">
          <Eye className="w-5 h-5 text-primary" />
          <div className="min-w-0">
            <h2 className="font-semibold text-slate-900 truncate">Visual Preview · {tpl.name}</h2>
            <p className="text-xs text-slate-500 truncate">Live render of unsaved changes using sample data{preview ? ` · ${preview.total} pages total` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {sample.length > 0 && (
            <select value={safeIdx} onChange={e => setIdx(Number(e.target.value))} className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm max-w-xs">
              {sample.map((s, i) => <option key={i} value={i}>/{s.slug}{i === 0 ? "  (full preview)" : "  (titles only)"}</option>)}
            </select>
          )}
          <div className="flex border border-slate-200 rounded-lg overflow-hidden">
            <button onClick={() => setDevice("desktop")} title="Desktop" className={`px-2.5 py-1.5 ${device === "desktop" ? "bg-primary text-white" : "text-slate-500 hover:bg-slate-50"}`}><Monitor className="w-4 h-4" /></button>
            <button onClick={() => setDevice("mobile")} title="Mobile" className={`px-2.5 py-1.5 ${device === "mobile" ? "bg-primary text-white" : "text-slate-500 hover:bg-slate-50"}`}><Smartphone className="w-4 h-4" /></button>
          </div>
          <button onClick={onRefresh} className="px-3 py-1.5 text-xs text-primary hover:bg-primary/5 rounded-lg font-medium">Refresh data</button>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 md:p-8 flex items-start justify-center" onClick={e => e.stopPropagation()}>
        {!current ? (
          <div className="text-white/80 py-20">Bind variables to collections to generate preview pages.</div>
        ) : (
          <div className={`bg-white rounded-2xl shadow-2xl overflow-hidden w-full ${device === "mobile" ? "max-w-[420px]" : "max-w-6xl"}`}>
            <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              <code className="text-xs text-slate-500 ml-2 truncate">vertexclimate.co.uk/{current.slug}</code>
            </div>
            <div className="p-6 md:p-10">
              {safeIdx !== 0 && (
                <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 rounded-lg">
                  Showing slug/title for this sample. Pick the first sample to render the full page with all field data.
                </div>
              )}
              <div className="mb-6 pb-4 border-b border-slate-100">
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">SEO Title</div>
                <h1 className="text-2xl font-bold text-slate-900">{renderedTitle}</h1>
                {renderedMeta && <p className="text-sm text-slate-500 mt-1">{renderedMeta}</p>}
              </div>
              <PageRenderer blocks={renderedBlocks} context={ctx} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewPanel({ preview, onRefresh }: { preview: { total: number; sample: any[] } | null; onRefresh: () => void }) {
  return (
    <div className="lg:sticky lg:top-32 lg:self-start">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Pages Preview</h3>
          <button onClick={onRefresh} className="text-xs text-primary hover:underline">Refresh</button>
        </div>
        {preview == null ? (
          <div className="text-sm text-slate-400 py-6 text-center">Loading...</div>
        ) : preview.total === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">Bind variables to collections to see generated pages.</div>
        ) : (
          <>
            <div className="bg-emerald-50 text-emerald-800 text-sm px-3 py-2 rounded-lg mb-3 font-medium">{preview.total} pages will be generated</div>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {preview.sample.map((s, i) => (
                <a key={i} href={`/${s.slug}`} target="_blank" rel="noreferrer" className="block px-2 py-1.5 text-xs hover:bg-slate-50 rounded group">
                  <div className="font-mono text-primary truncate">/{s.slug}</div>
                  {s.seoTitle && <div className="text-slate-500 truncate">{s.seoTitle}</div>}
                </a>
              ))}
            </div>
            {preview.total > preview.sample.length && <div className="text-xs text-slate-400 mt-2 text-center">+ {preview.total - preview.sample.length} more</div>}
          </>
        )}
      </div>
    </div>
  );
}

function EntryFilter({ collectionSlug, selected, onChange }: { collectionSlug: string; selected: string[]; onChange: (s: string[]) => void }) {
  const [entries, setEntries] = useState<{ slug: string; title: string }[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let cancel = false;
    fetch(`${API}/collections/${collectionSlug}/entries`).then(r => r.ok ? r.json() : null).then(data => {
      if (cancel) return;
      const rows = Array.isArray(data) ? data : Array.isArray(data?.entries) ? data.entries : [];
      setEntries(rows.map((e: any) => ({ slug: e.slug, title: e.title })));
    }).catch(() => {});
    return () => { cancel = true; };
  }, [collectionSlug]);
  const allSelected = selected.length === 0;
  const isChecked = (slug: string) => allSelected || selected.includes(slug);
  const toggle = (slug: string) => {
    if (allSelected) {
      onChange(entries.filter(e => e.slug !== slug).map(e => e.slug));
      return;
    }
    const next = selected.includes(slug) ? selected.filter(s => s !== slug) : [...selected, slug];
    if (entries.length > 0 && next.length === entries.length) { onChange([]); return; }
    onChange(next);
  };
  return (
    <div className="pl-2">
      <button type="button" onClick={() => setOpen(o => !o)} className="text-xs text-slate-500 hover:text-primary">
        Filter entries: <span className="font-medium text-slate-700">{allSelected ? `All (${entries.length})` : `${selected.length} of ${entries.length}`}</span> {open ? "▲" : "▼"}
      </button>
      {open && (
        <div className="mt-2 border border-slate-200 rounded-lg p-2 bg-slate-50 max-h-48 overflow-y-auto">
          <div className="flex gap-2 mb-2 text-xs">
            <button type="button" onClick={() => onChange([])} className="text-primary hover:underline">Include all</button>
            <button type="button" onClick={() => onChange(entries.length > 0 ? [entries[0].slug] : [])} className="text-slate-500 hover:underline">Clear (keep 1)</button>
          </div>
          {entries.length === 0 ? <div className="text-xs text-slate-400">No entries yet.</div> : entries.map(e => (
            <label key={e.slug} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer hover:bg-white px-1 rounded">
              <input type="checkbox" checked={isChecked(e.slug)} onChange={() => toggle(e.slug)} />
              <span className="truncate">{e.title}</span>
              <span className="text-xs text-slate-400 font-mono ml-auto truncate">{e.slug}</span>
            </label>
          ))}
          <p className="text-xs text-slate-400 mt-2 px-1">All checked = every entry is included. Uncheck to limit this template to specific ones (e.g. only Air Conditioning).</p>
        </div>
      )}
    </div>
  );
}
